import type {
  AyzeoCollectionBatch,
  AyzeoPdfPromptSnapshot,
  AyzeoPromptPlatformRun,
  PlannedPrompt,
  PortalState,
  Report,
} from "./domain";

export interface ResolvedAuditPrompt {
  id: string;
  promptId: string;
  wording: string;
  order: number;
  group: string;
  purpose: string;
  geographicScope: string;
  priority: PlannedPrompt["priority"];
  reasonForInclusion: string;
  pdfEvidence?: AyzeoPdfPromptSnapshot;
}

export interface SectionFourEvidenceResolution {
  status: "Resolved" | "Blocked";
  prompts: ResolvedAuditPrompt[];
  batch?: AyzeoCollectionBatch;
  runs: AyzeoPromptPlatformRun[];
  orphanRuns: AyzeoPromptPlatformRun[];
  warnings: string[];
  blockers: string[];
}

const usableBatch = (batch: AyzeoCollectionBatch) =>
  batch.status === "Complete" || batch.status === "Partially complete";

const batchTime = (batch: AyzeoCollectionBatch) =>
  Date.parse(batch.retrievedAt ?? "") || 0;

export function resolveReportAuditEngagementId(state: PortalState, report: Report): string | undefined {
  if (report.auditEngagementId) return report.auditEngagementId;
  const workflows = state.onboardingWorkflows.filter(item =>
    item.organisationId === report.organisationId && item.auditEngagementId,
  );
  const directlyLinked = workflows.filter(item => item.startedReportId === report.id);
  if (directlyLinked.length === 1) return directlyLinked[0].auditEngagementId;
  const withImportedEvidence = workflows.filter(item =>
    state.ayzeoCollectionBatches.some(batch =>
      batch.auditEngagementId === item.auditEngagementId && usableBatch(batch),
    ),
  );
  if (withImportedEvidence.length === 1) return withImportedEvidence[0].auditEngagementId;
  return workflows.length === 1 ? workflows[0].auditEngagementId : undefined;
}

export function resolveSectionFourEvidence(
  state: PortalState,
  auditEngagementId?: string,
  selectedCollectionBatchId?: string,
): SectionFourEvidenceResolution {
  const blockers: string[] = [];
  const warnings: string[] = [];
  if (!auditEngagementId) {
    return {
      status: "Blocked",
      prompts: [],
      runs: [],
      orphanRuns: [],
      warnings,
      blockers: ["The active report is not linked to an audit engagement. The stored evidence has not been changed."],
    };
  }

  const onboarding = state.onboardingWorkflows.find(
    item => item.auditEngagementId === auditEngagementId,
  );
  if (!onboarding) {
    blockers.push("No onboarding workflow is linked to this audit. Link the existing workflow before reviewing prompts.");
  }

  const approved = [...(onboarding?.prompts ?? [])]
    .filter(item => item.approved)
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  if (!approved.length) {
    blockers.push("No approved onboarding prompts are linked to this audit. Approve the stored prompt set before continuing.");
  }

  const pdfByPromptId = new Map(
    state.ayzeoPdfPromptSnapshots
      .filter(item => item.auditEngagementId === auditEngagementId)
      .map(item => [item.promptId, item]),
  );
  const prompts: ResolvedAuditPrompt[] = approved.map(prompt => {
    const pdfEvidence = pdfByPromptId.get(prompt.id);
    return {
      id: pdfEvidence?.id ?? prompt.id,
      promptId: prompt.id,
      wording: prompt.wording,
      order: prompt.order,
      group: prompt.group,
      purpose: prompt.commercialPurpose,
      geographicScope: prompt.geographicScope,
      priority: prompt.priority,
      reasonForInclusion: prompt.reason,
      pdfEvidence,
    };
  });

  const batches = state.ayzeoCollectionBatches
    .filter(item => item.auditEngagementId === auditEngagementId && usableBatch(item))
    .sort((a, b) => batchTime(b) - batchTime(a) || b.id.localeCompare(a.id));
  const selected = batches.find(item => item.id === selectedCollectionBatchId);
  const latestReal = batches.find(item => item.source === "Ayzeo read adapter");
  const batch = selected?.source === "Ayzeo read adapter"
    ? selected
    : latestReal ?? selected ?? batches[0];

  if (!batch) {
    blockers.push("No completed evidence collection is linked to this audit. Import the authorised read-only retrieval before continuing.");
  } else if (batch.source !== "Ayzeo read adapter") {
    warnings.push("The selected collection is local demonstration evidence, not an authorised read-only Ayzeo retrieval.");
  } else if (selectedCollectionBatchId && selectedCollectionBatchId !== batch.id) {
    warnings.push("The earlier selected collection was unavailable or not real evidence, so the latest successful Ayzeo retrieval is shown.");
  }

  const batchRuns = batch
    ? state.ayzeoPromptPlatformRuns.filter(item => item.collectionBatchId === batch.id)
    : [];
  const approvedIds = new Set(approved.map(item => item.id));
  const runs = batchRuns.filter(item => approvedIds.has(item.promptId));
  const orphanRuns = batchRuns.filter(item => !approvedIds.has(item.promptId));
  if (orphanRuns.length) {
    warnings.push(`${orphanRuns.length} imported execution${orphanRuns.length === 1 ? " is" : "s are"} not linked to an approved prompt and remain preserved outside the review list.`);
  }

  return {
    status: blockers.length ? "Blocked" : "Resolved",
    prompts,
    batch,
    runs,
    orphanRuns,
    warnings,
    blockers,
  };
}
